import {
  Dispensary,
  DispensaryAccountReviewWithDataV2,
  MinifiedDispensary,
  MUO,
  OrganizationType
} from '@gcv/shared';
import { Box } from '@mui/material';
import { useComponent } from 'hooks';
import * as React from 'react';
import {
  FilterListChild,
  FilterListItem,
  FilterSelect,
  FilterSelectButton,
  FilterSelectOption,
  InlineSpinner,
  InputLabel
} from 'ui';
import { actions } from './actions';
import { model } from './model';

interface Props extends Record<string, unknown> {
  currentOrg: MinifiedDispensary | Dispensary | MUO;
  filterBy?: (filters: FilterListItem[]) => FilterListItem[];
  headerComponent?: boolean;
  label?: string;
  noMargin?: boolean;
  onChange?: (selected: FilterListChild[]) => void;
  index?: string;
  reviewNumber?: number;
  review?: DispensaryAccountReviewWithDataV2;
}

export const AccountNumberLocationFilter: React.FC<Props> = useComponent((props) => {
  const [filterList, setFilterList] = React.useState<FilterSelectOption[]>([]);
  const [filterToggle, setFilterToggle] = React.useState(false);
  const [selectedFilters, setSelectedFilters] = React.useState<FilterSelectOption[]>([]);

  React.useEffect(() => {
    actions.clearActivityFilters();

    actions.getActivityFilters(props.currentOrg.id, props.review, props.index).then(() => {
      actions.updateAppViewStore();
      let filters = getInitializedFilters();

      if (props.filterBy) {
        if (!props.index || !props.reviewNumber || props.reviewNumber > Number(props.index)) {
          filters = props.filterBy([...filters]);
        }
      }

      setFilterList(filters);
      actions.setPreviousFilters(filters);
    });
  }, [props.currentOrg.id]);

  React.useEffect(() => {
    if (model.currentOrg.orgType === OrganizationType.MUO && model.currentOrg.shared_internal_id) {
      setSelectedFilters(model.selectedAccountNumbers.concat(model.selectedLocations));
    } else {
      setSelectedFilters(model.selectedAccountNumbers);
    }
  }, [model.loadingActivityFilters]);

  const getInitializedFilters = () => {
    if (props.index !== undefined && !model.filterArray[props.index]) {
      model.filterArray[props.index] = {
        accountNumberFilter: [],
        accountsFilter: [],
        salesLocationFilter: []
      };
    }

    const filters: FilterListItem[] = [];

    if (props.currentOrg.orgType === OrganizationType.MUO && props.currentOrg.shared_internal_id) {
      if (
        props.index !== undefined
          ? model.filterArray[props.index].accountNumberFilter.length
          : model.accountNumberFilter.length
      ) {
        filters.push({
          label: 'Account Numbers',
          value: 'account_numbers',
          children:
            props.index !== undefined
              ? model.filterArray[props.index].accountNumberFilter
              : model.accountNumberFilter
        });
      }

      filters.push({
        label: 'Sales Location',
        value: 'sales_location',
        children:
          props.index !== undefined
            ? model.filterArray[props.index].salesLocationFilter
            : model.salesLocationFilter
      });
    } else {
      if (props.index !== undefined) {
        filters.push(...model.filterArray[props.index].accountsFilter);
      } else {
        filters.push(...model.accountsFilter);
      }
    }

    return filters;
  };

  const toggleFilter = () => {
    setFilterToggle(!filterToggle);
  };

  const onApply = (selected: FilterSelectOption[]) => {
    actions.setFetchData(false);

    if (props.index !== undefined) {
      actions.selectFiltersIndexed(props.index, selected);
    } else {
      actions.selectFilters(selected);
    }

    toggleFilter();
    //This logic is used to initialize the selectedFilters and has been added to avoid
    //counting location filters as accounts when changing the selected filters.
    //this value is for the filterSelectButton that displays a label with # of selected accounts.
    if (model.currentOrg.orgType === OrganizationType.MUO && model.currentOrg.shared_internal_id) {
      setSelectedFilters(selected);
    } else {
      const selectedAccounts = selected.filter((selectedFilter) =>
        model.accountNumberFilter.some((accountFilter) => accountFilter.value === selectedFilter.value)
      );
      setSelectedFilters(selectedAccounts);
    }
    //getInitializedFilters will pull from the model.
    //After model is updated above in actions.selectFilters, update the filters for the FilterSelect
    const filters = getInitializedFilters();
    // iterate over each filter and reselect it if it was selected before
    for (const filter of filters) {
      const selectedFilter = selected.find((f) => f.value === filter.value);

      if (selectedFilter) {
        filter.selected = selectedFilter.selected;
      }

      if (filter.children) {
        for (const child of filter.children) {
          const selectedChild = selected.find((f) => f.value === child.value);

          if (selectedChild) {
            child.selected = selectedChild.selected;
          }
        }
      }
    }

    setFilterList(filters);
    actions.setPreviousFilters(filters);

    if (props.onChange) {
      props.onChange(selected);
    }

    actions.setFetchData(true);
  };

  const onCancel = () => {
    setTimeout(() => setFilterToggle(false), 1);

    let prevList = [];

    if (model.previousFilters && model.previousFilters.length > 0) {
      prevList = JSON.parse(JSON.stringify(model.previousFilters));
    } else {
      prevList = getInitializedFilters();
    }

    setFilterList(prevList);
  };

  if (
    model.loadingActivityFilters ||
    (!model.accountNumberFilter.length && model.salesLocationFilter.length === 1)
  ) {
    return (
      <Box sx={{ marginTop: '30px' }}>
        <InlineSpinner size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {props.label && (
        <Box sx={{ mb: '6px' }}>
          <InputLabel name="account-number-filter-label" label={props.label} />
        </Box>
      )}
      <FilterSelectButton
        filterToggle={filterToggle}
        toggleFilter={toggleFilter}
        selectedFilters={selectedFilters}
        headerComponent={props.headerComponent}
        noMargin={props.noMargin}
      />
      {filterToggle ? (
        <FilterSelect
          transform={'selected'}
          options={filterList}
          onApply={onApply}
          onCancel={onCancel}
          config={{
            title: 'Accounts',
            height: '250px',
            emitAllOnEmpty: true,
            emitExcludeParent: true,
            clearChildren: { isHidden: false },
            count: { isHidden: true },
            search: { isHidden: true },
            selectAllClearAll: { isHidden: false, position: 'top' },
            showSelectedOnly: { isHidden: true },
            showHidden: { isHidden: true, label: '' },
            type: 'dropdown'
          }}
        />
      ) : null}
    </Box>
  );
});
